import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from '@lore/auth';
import { useQueryMutation } from '@lore/query-connect';
import { PayloadStates } from '@lore/utils';
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import Loader from '@/components/Loader';
import ToggleCard from '@/components/ui/ToggleCard';
import TagTypes from '@/constants/TagTypes';
import useTags from '@/hooks/useTags';
import useUserTags from '@/hooks/useUserTags';
import EditableGrades from '@/pages/profile-edit/_streamlined/EditableGrades';
import EditableSkills from '@/pages/profile-edit/_streamlined/EditableSkills';
import EditableSubjects from '@/pages/profile-edit/_streamlined/EditableSubjects';
import {useTranslation} from 'react-i18next';
import { marked } from 'marked';
import { useWizard } from '../WizardContext';

export default function YourInvolvement() {
  const {t} = useTranslation();
  const currentUser = useUser();
  const { setNextDisabled } = useWizard();

  const intents = useTags(TagTypes.Intent);
  const userIntents = useUserTags(currentUser, TagTypes.Intent);
  const queryClient = useQueryClient();
  const userGrades = useUserTags(currentUser, TagTypes.Grade)
  const [hasGrades, setHasGrades] = useState(false);
  const userSubjects = useUserTags(currentUser, TagTypes.Subject);
  const [hasSubjects, setHasSubjects] = useState(false);

  const userExpertises = useUserTags(currentUser, TagTypes.Expertise);
  const [hasExpertises, setHasExpertises] = useState(false);

  const hasGradesSet = React.useRef(false);
  const hasSubjectsSet = React.useRef(false);
  const hasExpertisesSet = React.useRef(false);

  useEffect(() => {
    if (!hasGradesSet.current && userGrades.state === PayloadStates.RESOLVED) {
      setHasGrades(userGrades.data && userGrades.data.length > 0);
      hasGradesSet.current = true;
    }
  }, [userGrades.state, userGrades.data]);

  useEffect(() => {
    if (!hasSubjectsSet.current && userSubjects.state === PayloadStates.RESOLVED) {
      setHasSubjects(userSubjects.data && userSubjects.data.length > 0);
      hasSubjectsSet.current = true;
    }
  }, [userSubjects.state, userSubjects.data]);

  useEffect(() => {
    if (!hasExpertisesSet.current && userExpertises.state === PayloadStates.RESOLVED) {
      setHasExpertises(userExpertises.data && userExpertises.data.length > 0);
      hasExpertisesSet.current = true;
    }
  }, [userExpertises.state, userExpertises.data]);

  const mutationCreate = useQueryMutation('tagNode.create');
  const mutationDestroy = useQueryMutation('tagNode.destroy2');

  const currentUserEducatorIntent = useMemo(
    () => _.find(userIntents.data, (ui) => ui.data.name === 'Educator'),
    [userIntents.data]
  );
  const currentUserPartnerIntent = useMemo(
    () => _.find(userIntents.data, (ui) => ui.data.name === 'Partner'),
    [userIntents.data]
  );

  const [isEducator, setIsEducator] = useState(Boolean(currentUserEducatorIntent));
  const [isPartner, setIsPartner] = useState(Boolean(currentUserPartnerIntent));
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    if (userIntents.state === PayloadStates.FETCHING || isChanging || mutationCreate.isLoading || mutationDestroy.isLoading) return;
    setIsEducator(Boolean(currentUserEducatorIntent));
  }, [currentUserEducatorIntent, mutationCreate.isLoading, mutationDestroy.isLoading, userIntents.state, isChanging]);

  useEffect(() => {
    if (userIntents.state === PayloadStates.FETCHING || isChanging || mutationCreate.isLoading || mutationDestroy.isLoading) return;
    setIsPartner(Boolean(currentUserPartnerIntent));
  }, [currentUserPartnerIntent, mutationCreate.isLoading, mutationDestroy.isLoading, userIntents.state, isChanging]);

  const onChangeEducator = useCallback(async (checked) => {
    try {
      setIsChanging(true);
      setIsEducator(checked);
      const tag = _.find(intents.data, (t) => t.data.name === 'Educator');
      if (checked && !currentUserEducatorIntent && tag) {
        await mutationCreate.mutateAsync({ tagId: tag.id, userId: currentUser.id });
        await userIntents._invalidateQuery();
        queryClient.invalidateQueries(['currentUser']);
      } else if (!checked && currentUserEducatorIntent) {
        await mutationDestroy.mutateAsync(currentUserEducatorIntent._tagNode);
        await userIntents._invalidateQuery();
        queryClient.invalidateQueries(['currentUser']);
      }
    } finally {
      setIsChanging(false);
    }
  }, [currentUser.id, mutationCreate, mutationDestroy, intents.data, currentUserEducatorIntent, userIntents, queryClient]);

  const onChangePartner = useCallback(async (checked) => {
    try {
      setIsChanging(true);
      setIsPartner(checked);
      const tag = _.find(intents.data, (t) => t.data.name === 'Partner');
      if (checked && !currentUserPartnerIntent && tag) {
        await mutationCreate.mutateAsync({ tagId: tag.id, userId: currentUser.id });
        await userIntents._invalidateQuery();
        queryClient.invalidateQueries(['currentUser']);
      } else if (!checked && currentUserPartnerIntent) {
        await mutationDestroy.mutateAsync(currentUserPartnerIntent._tagNode);
        await userIntents._invalidateQuery();
        queryClient.invalidateQueries(['currentUser']);
      }
    } finally {
      setIsChanging(false);
    }
  }, [currentUser.id, mutationCreate, mutationDestroy, intents.data, currentUserPartnerIntent, userIntents, queryClient]);


  useEffect(() => {
    const isGradesValid = userGrades.data && userGrades.data.length > 0;
    const isSubjectsValid = userSubjects.data && userSubjects.data.length > 0;
    const educatorInvalid = isEducator && (!isGradesValid || !isSubjectsValid);
    const isExpertisesValid = userExpertises.data && userExpertises.data.length > 0;
    const partnerInvalid = isPartner && !isExpertisesValid;
    setNextDisabled(educatorInvalid || partnerInvalid);
  }, [isEducator, isPartner, setNextDisabled, userGrades.data, userSubjects.data, userExpertises.data]);


  if (intents.state === PayloadStates.FETCHING ||
    userIntents.state === PayloadStates.FETCHING ||
    userGrades.state === PayloadStates.FETCHING ||
    userSubjects.state === PayloadStates.FETCHING ||
    userExpertises.state === PayloadStates.FETCHING
  ) {
    return <Loader />;
  }

  return (
    <div className="flex-1 px-6 py-8 space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold text-cs-gray-900">
          How do you want to get involved?
        </h2>
        <p className="text-sm text-cs-gray-500">
          Use the switches below to select the activities that interest you so we can connect you to the right opportunities.
        </p>
      </div>

      <div className="space-y-6 ">
        <div className="space-y-4 items-start grid grid-cols-1 md:grid-cols-3 gap-6 bg-cs-gray-50 rounded-lg p-4">
          <ToggleCard
            label="Creating projects and events for learners I teach"
            checked={isEducator}
            onChange={onChangeEducator}
          />
          <div className="col-span-2 space-y-4 pl-1">
            <p className="text-base markdown-content"
               dangerouslySetInnerHTML={{ __html: marked.parse(t('account.educator.description')) }}>
            </p>
            <div className={`grid transition-all duration-300 ease-in-out ${isEducator ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
              <div className="overflow-hidden space-y-4">
                {!hasSubjects && <EditableSubjects showEditToggle={false} />}
                {/*<pre>{JSON.stringify(userGrades.data, null, 2)} </pre>*/}
                {!hasGrades && <EditableGrades showEditToggle={false} /> }
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 items-start grid grid-cols-1 md:grid-cols-3 gap-6 bg-cs-gray-50 rounded-lg p-4">
          <ToggleCard
            label="Volunteering to share my skills with learners and educators"
            checked={isPartner}
            onChange={onChangePartner}
          />
          <div className="col-span-2 space-y-4 pl-1">
            <p className="text-base markdown-content"
               dangerouslySetInnerHTML={{ __html: marked.parse(t('account.partner.description')) }}>
            </p>
            <div className={`grid transition-all duration-300 ease-in-out ${isPartner ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
              <div className="overflow-hidden space-y-4">
                {!hasExpertises && <EditableSkills />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
